import { toast } from 'sonner';
import { create } from 'zustand';

import {
  hydrateDataWidgetBlocksForSection,
  hydrateDataWidgetBlocksForSections,
} from '@/lib/blocks/collection/data-widget/hydration';
import http from '@/lib/http';
import { scheduleScrollToPageEditorSection } from '@/lib/page-editor/scroll-to-section';
import { getMainSections } from '@/lib/page-utils';
import {
  acceptCmsPagesEditorAiProposalBatchPath,
  cmsPagesEditorAiProposalPath,
  cmsPagesEditorAiProposalsPath,
  materializeCmsPagesEditorAiProposalPath,
  rejectCmsPagesEditorAiProposalBatchPath,
} from '@/lib/routes';
import { isSpecialPageLayoutSection } from '@/lib/special-page-layouts';
import {
  flushPendingAutosave,
  pauseAutosave,
  resumeAutosave,
} from '@/stores/page-editor-middleware/queue';
import { usePageEditorStore } from '@/stores/page-editor-store';
import type { AiProposal } from '@/types/ai-proposal';
import type { PageSection } from '@/types/page-section';
import {
  buildBatchGroups,
  firstTargetedProposalIds,
  isActiveProposalStatus,
  isPreviewableAddProposal,
  isPreviewableTargetedProposal,
  isProposalAlreadyResolved,
  payloadContainsRunId,
  payloadHasAnyRunId,
  resolveRunIdInPayload,
  sortProposals,
  toPageSectionPayload,
  toPreviewSectionSnapshot,
  toPreviewSectionUpdates,
} from './ai-proposals/helpers';
import { mergeSpecialPageDeltaBlocks } from './ai-proposals/special-page-block-merge';

export type { AiProposal };

type AiProposalsStore = {
  proposals: AiProposal[];
  isLoading: boolean;
  isMutating: boolean;
  inFlightIds: Set<string>;
  // Maps add-proposal id → preview section id for optimistic add previews.
  previewSectionIds: Map<string, string>;
  // Tracks proposals whose preview creation is in progress (async not yet resolved).
  previewPendingIds: Set<string>;
  // Captures the actual section state from the editor store BEFORE a targeted proposal preview
  // is applied, so revert-on-reject always uses the true pre-proposal state (not the server's
  // beforeSnapshot, which may be stale if e.g. a prior image run hasn't written to DB yet).
  clientSnapshots: Map<string, Partial<PageSection>>;
  // Per-section committed baseline, captured while the section is clean and preserved across proposal churn, for the diff/merge.
  sectionDiffBaselines: Map<string, Partial<PageSection>>;
  // Proposal IDs that the user has already accepted or rejected — prevents message-driven
  // re-adds (e.g. when AI updates a proposal's status while the user is already acting on it).
  resolvedProposalIds: Set<string>;
  lastLoadParams: { pageId: string; pageVersionId?: string; threadId?: string } | null;
  setProposals: (proposals: AiProposal[]) => void;
  addProposal: (proposal: AiProposal) => void;
  removeProposal: (proposalId: string) => void;
  getOriginalSnapshot: (proposalId: string) => Partial<PageSection> | null;
  getSectionDiffBaseline: (sectionId: string) => Partial<PageSection> | null;
  hasProposalRunId: (runId: string) => boolean;
  resolveProposalRunId: (runId: string, imageUrl: string) => void;
  loadProposals: (pageId: string, pageVersionId?: string, threadId?: string) => Promise<void>;
  reloadProposals: () => Promise<void>;
  accept: (proposalId: string) => Promise<void>;
  reject: (proposalId: string) => Promise<void>;
  acceptMany: (proposalIds: string[]) => Promise<void>;
  rejectMany: (proposalIds: string[]) => Promise<void>;
  acceptAll: (pageId: string, batchId?: string) => Promise<boolean>;
  rejectAll: (pageId: string, batchId?: string) => Promise<void>;
  // Re-applies all pending proposal previews after setPage() resets sections to server state.
  // Called from editor.tsx whenever Inertia delivers new page props (including deferred props).
  reapplyPendingPreviews: () => void;
  // Remaps block IDs in clientSnapshots when a draft is created from a live version.
  // clientSnapshots are captured with live block IDs; after setPageForAutosaveRemap the proposal
  // payload uses draft block IDs, so computeSectionDiff would see a mismatch without this remap.
  remapClientSnapshotsForDraft: (blocksMapping: Record<string, string>) => void;
};

function getCurrentMainSectionsSorted(): PageSection[] {
  const sections = usePageEditorStore.getState().page?.slots?.main?.sections;
  if (!Array.isArray(sections)) return [];

  return [...sections].sort((left, right) => left.rowPosition - right.rowPosition);
}

function getLivePreviewRowPosition(proposalId: string): number | undefined {
  const previewSectionId = useAiProposalsStore.getState().previewSectionIds.get(proposalId);
  if (!previewSectionId) return undefined;

  const sections = getCurrentMainSectionsSorted();
  const previewIndex = sections.findIndex((section) => section.id === previewSectionId);
  return previewIndex >= 0 ? previewIndex : undefined;
}

function resolveAddInsertRowPosition(proposal: AiProposal): number | undefined {
  const livePreviewRowPosition = getLivePreviewRowPosition(proposal.id);
  if (typeof livePreviewRowPosition === 'number') {
    return livePreviewRowPosition;
  }

  return typeof proposal.payload.rowPosition === 'number'
    ? (proposal.payload.rowPosition as number)
    : undefined;
}

function orderProposalsForApply(proposals: AiProposal[]): AiProposal[] {
  const indexed = proposals.map((proposal, index) => ({ proposal, index }));

  return indexed
    .sort((left, right) => {
      const leftIsAdd = left.proposal.operation === 'add';
      const rightIsAdd = right.proposal.operation === 'add';

      if (leftIsAdd && rightIsAdd) {
        const leftRow = resolveAddInsertRowPosition(left.proposal) ?? Number.MAX_SAFE_INTEGER;
        const rightRow = resolveAddInsertRowPosition(right.proposal) ?? Number.MAX_SAFE_INTEGER;
        if (leftRow !== rightRow) return leftRow - rightRow;
      }

      return left.index - right.index;
    })
    .map(({ proposal }) => proposal);
}

// Captures the current state of a targeted section from the page editor store, to use as
// the authoritative "before" snapshot for client-side revert on reject.
function captureClientSnapshot(targetSectionId: string): Partial<PageSection> | null {
  const sections = usePageEditorStore.getState().page?.slots?.main?.sections;
  if (!sections) return null;
  const section = sections.find((s) => s.id === targetSectionId);
  if (!section) return null;
  return {
    sectionTemplateCode: section.sectionTemplateCode,
    variant: section.variant,
    props: section.props,
    blocks: section.blocks,
    visible: section.visible,
  };
}

// Drop baselines for sections with no remaining previewable proposal, so the next edit recaptures the (post-accept) committed state.
function pruneSectionDiffBaselines(
  proposals: AiProposal[],
  baselines: Map<string, Partial<PageSection>>,
): Map<string, Partial<PageSection>> {
  const activeSectionIds = new Set(
    proposals.flatMap((p) =>
      isPreviewableTargetedProposal(p) && p.targetSectionId ? [p.targetSectionId] : [],
    ),
  );
  return new Map([...baselines].filter(([sectionId]) => activeSectionIds.has(sectionId)));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// Mirror accepted publish timestamps into the toolbar's editor input, including changes to time alone.
function syncAcceptedPublishDate(updates: Partial<PageSection>): Partial<PageSection> {
  const props = isRecord(updates.props) ? updates.props : undefined;
  const contentModel = props && isRecord(props.contentModel) ? props.contentModel : undefined;
  if (!contentModel || !('publishedAt' in contentModel)) return updates;

  const page = usePageEditorStore.getState().page;
  const nextPublishedAt = contentModel.publishedAt;
  if (nextPublishedAt === page?.contentModel?.editorPublishAt) {
    return updates;
  }

  usePageEditorStore.getState().syncPageLevelContentModel({
    editorPublishAt: nextPublishedAt,
    publishedAt: nextPublishedAt,
  });
  return {
    ...updates,
    props: { ...props, contentModel: { ...contentModel, editorPublishAt: nextPublishedAt } },
  };
}

async function applyProposalToEditorStore(
  proposal: AiProposal,
  options?: { insertRowPosition?: number },
): Promise<string | null> {
  const editorStore = usePageEditorStore.getState();

  switch (proposal.operation) {
    case 'add': {
      const newSectionId = await editorStore.addSection(
        toPageSectionPayload(proposal.payload),
        options?.insertRowPosition,
        { skipDefaultHydration: true },
      );
      return newSectionId;
    }
    case 'update':
      if (proposal.targetSectionId) {
        const { changedSlots: _changedSlots, ...payload } = proposal.payload;
        const updates = specialPageDeltaUpdates(
          proposal.targetSectionId,
          payload as Partial<PageSection>,
          readChangedSlots(proposal.payload),
        );
        editorStore.updateSection(proposal.targetSectionId, syncAcceptedPublishDate(updates));
      }
      return null;
    case 'delete':
      if (proposal.targetSectionId) {
        editorStore.deleteSection(proposal.targetSectionId);
      }
      return null;
    case 'replace':
      if (proposal.targetSectionId) {
        editorStore.replaceSection(
          proposal.targetSectionId,
          toPageSectionPayload(proposal.payload),
        );
      }
      return null;
    case 'reorder':
      if (Array.isArray(proposal.payload.moves)) {
        editorStore.moveSectionsToRowPositions(
          proposal.payload.moves as Array<{ sectionId: string; targetRowPosition: number }>,
        );
      }
      return null;
  }
}

function revertTargetedProposalSection(proposalId: string): void {
  const state = useAiProposalsStore.getState();
  const proposal = state.proposals.find((p) => p.id === proposalId);
  if (!proposal?.targetSectionId) return;

  const originalUpdates = originalPreviewUpdatesForProposal(proposalId);
  if (!originalUpdates) return;

  usePageEditorStore.getState().replaceSectionPreview(proposal.targetSectionId, originalUpdates);
}

function originalPreviewUpdatesForProposal(proposalId: string): Partial<PageSection> | null {
  const state = useAiProposalsStore.getState();
  const proposal = state.proposals.find((p) => p.id === proposalId);
  if (!proposal?.targetSectionId) return null;

  // Prefer the client-captured snapshot — it's the actual editor state before the preview
  // was applied, so it's always correct regardless of DB timing (e.g. stale run_id in
  // beforeSnapshot when ImageRunWebhookJob hasn't written the completed URL yet).
  const clientSnapshot = state.clientSnapshots.get(proposalId);
  if (clientSnapshot) {
    return clientSnapshot;
  }

  // Fall back to the server-provided beforeSnapshot.
  if (proposal.beforeSnapshot) {
    return toPreviewSectionSnapshot(proposal.beforeSnapshot);
  }

  return null;
}

// Rewrite a special-page payload's blocks to only the slots it changes vs the committed baseline, so siblings survive preview and accept.
function specialPageDeltaUpdates(
  sectionId: string,
  updates: Partial<PageSection>,
  changedSlots?: string[],
): Partial<PageSection> {
  if (!Array.isArray(updates.blocks)) return updates;
  const page = usePageEditorStore.getState().page;
  const section = page ? getMainSections(page).find((sec) => sec.id === sectionId) : undefined;
  if (!section || !isSpecialPageLayoutSection(section)) return updates;

  const baseline = useAiProposalsStore.getState().sectionDiffBaselines.get(sectionId);
  return {
    ...updates,
    blocks: mergeSpecialPageDeltaBlocks({
      current: section.blocks ?? [],
      original: Array.isArray(baseline?.blocks) ? baseline.blocks : [],
      proposed: updates.blocks,
      changedSlots,
    }) as PageSection['blocks'],
  };
}

// Slots a proposal declares it means to change; absent on proposals created before the field existed.
// An empty list names nothing, so it falls back to the baseline comparison rather than suppressing every slot.
function readChangedSlots(payload: Record<string, unknown>): string[] | undefined {
  const slots = payload.changedSlots;
  return Array.isArray(slots) && slots.length > 0 && slots.every((slot) => typeof slot === 'string')
    ? (slots as string[])
    : undefined;
}

function applySuggestedTargetedProposalSection(proposalId: string): void {
  const state = useAiProposalsStore.getState();
  const proposal = state.proposals.find((p) => p.id === proposalId);
  if (!proposal?.targetSectionId || !isPreviewableTargetedProposal(proposal)) return;

  const updates = specialPageDeltaUpdates(
    proposal.targetSectionId,
    toPreviewSectionUpdates(proposal.payload),
    readChangedSlots(proposal.payload),
  );
  usePageEditorStore.getState().updateSectionPreview(proposal.targetSectionId, updates);
  void hydrateDataWidgetBlocksForSection(proposal.targetSectionId, { previewOnly: true });
}

function cleanupPreviewSection(proposalId: string): void {
  const state = useAiProposalsStore.getState();
  const proposal = state.proposals.find((p) => p.id === proposalId);

  if (!proposal) {
    return;
  }

  if (isPreviewableTargetedProposal(proposal) && proposal.targetSectionId) {
    revertTargetedProposalSection(proposalId);
    return;
  }

  const sectionId = state.previewSectionIds.get(proposalId);
  if (sectionId) {
    usePageEditorStore.getState().removePreviewSection(sectionId);
  }
}

async function preHydrateAddProposal(proposal: AiProposal): Promise<AiProposal> {
  const previewSectionId = useAiProposalsStore.getState().previewSectionIds.get(proposal.id);
  if (!previewSectionId) return proposal;

  await hydrateDataWidgetBlocksForSection(previewSectionId, { previewOnly: true });

  const page = usePageEditorStore.getState().page;
  const previewSection = page
    ? getMainSections(page).find((sec) => sec.id === previewSectionId)
    : null;
  if (!previewSection?.blocks) return proposal;

  return { ...proposal, payload: { ...proposal.payload, blocks: previewSection.blocks } };
}

// Collections whose members a metadata proposal can name before the record exists.
const PENDING_METADATA_KEYS = ['authors', 'attorneys', 'courts', 'practiceAreas', 'topics'];

function hasPendingMetadataMembers(payload: Record<string, unknown>): boolean {
  const props = isRecord(payload.props) ? payload.props : undefined;
  const contentModel = props && isRecord(props.contentModel) ? props.contentModel : undefined;
  if (!contentModel) return false;

  return PENDING_METADATA_KEYS.some((key) => {
    const members = contentModel[key];
    return (
      Array.isArray(members) &&
      members.some((member) => isRecord(member) && !member.id && Boolean(member.name))
    );
  });
}

// A proposal can name an author or topic that doesn't exist yet — Rails persists it here, on accept,
// so rejecting one never grows the account catalog. The applied payload then carries real ids.
async function materializePendingMetadata(proposal: AiProposal): Promise<AiProposal> {
  if (!hasPendingMetadataMembers(proposal.payload)) return proposal;

  const response = await http.patch(
    materializeCmsPagesEditorAiProposalPath(proposal.pageId, proposal.id),
  );
  const materialized = response.data?.data as AiProposal | undefined;
  if (!materialized) return proposal;

  useAiProposalsStore.setState((state) => ({
    proposals: state.proposals.map((p) => (p.id === materialized.id ? materialized : p)),
  }));
  return materialized;
}

async function applyAcceptedProposal(
  incomingProposal: AiProposal,
  hydrationSectionIds: Set<string>,
  firstTargetedIds?: Set<string>,
): Promise<string | null> {
  const proposal = await materializePendingMetadata(incomingProposal);

  if (proposal.operation === 'add') {
    const insertRowPosition = resolveAddInsertRowPosition(proposal);
    const preparedProposal = await preHydrateAddProposal(proposal);
    cleanupPreviewSection(proposal.id);
    return await applyProposalToEditorStore(preparedProposal, { insertRowPosition });
  }

  if (isPreviewableTargetedProposal(proposal) && proposal.targetSectionId) {
    if (!firstTargetedIds || firstTargetedIds.has(proposal.id)) {
      revertTargetedProposalSection(proposal.id);
    }
    await applyProposalToEditorStore(proposal);
    hydrationSectionIds.add(proposal.targetSectionId);
    return proposal.targetSectionId;
  }

  await applyProposalToEditorStore(proposal);
  if (proposal.targetSectionId) hydrationSectionIds.add(proposal.targetSectionId);
  return proposal.targetSectionId ?? null;
}

function collectRejectHydration(
  proposal: AiProposal,
  hydrationSectionIds: Set<string>,
  firstTargetedIds: Set<string>,
): void {
  if (isPreviewableTargetedProposal(proposal) && proposal.targetSectionId) {
    if (firstTargetedIds.has(proposal.id)) {
      revertTargetedProposalSection(proposal.id);
      hydrationSectionIds.add(proposal.targetSectionId);
    }
    return;
  }
  cleanupPreviewSection(proposal.id);
}

async function persistBatchStatusByGroup(
  pageId: string,
  proposals: AiProposal[],
  status: 'accepted' | 'rejected',
): Promise<void> {
  const pathBuilder =
    status === 'accepted'
      ? acceptCmsPagesEditorAiProposalBatchPath
      : rejectCmsPagesEditorAiProposalBatchPath;

  for (const [batchId, batchProposals] of buildBatchGroups(proposals)) {
    await http.patch(pathBuilder(pageId, batchId), {
      ids: batchProposals.map((proposal) => proposal.id),
    });
  }
}

export const useAiProposalsStore = create<AiProposalsStore>((set, get) => ({
  proposals: [],
  isLoading: false,
  isMutating: false,
  inFlightIds: new Set<string>(),
  previewSectionIds: new Map<string, string>(),
  previewPendingIds: new Set<string>(),
  clientSnapshots: new Map<string, Partial<PageSection>>(),
  sectionDiffBaselines: new Map<string, Partial<PageSection>>(),
  resolvedProposalIds: new Set<string>(),
  lastLoadParams: null,

  reapplyPendingPreviews: () => {
    const { proposals } = get();
    if (proposals.length === 0) return;
    // setPage() just reset sections to server state, wiping any updateSectionPreview calls
    // and removing add-proposal preview sections. Reset the preview tracking so setProposals
    // will re-add preview sections and re-apply updateSectionPreview for each proposal.
    // Drop section baselines so they recapture from the freshly-reset (committed) sections.
    set({
      previewSectionIds: new Map(),
      previewPendingIds: new Set(),
      sectionDiffBaselines: new Map(),
    });
    get().setProposals([...proposals]);
  },

  remapClientSnapshotsForDraft: (blocksMapping) => {
    const { clientSnapshots } = get();
    if (clientSnapshots.size === 0) return;
    const nextSnapshots = new Map<string, Partial<PageSection>>();
    for (const [proposalId, snapshot] of clientSnapshots) {
      if (!snapshot.blocks?.length) {
        nextSnapshots.set(proposalId, snapshot);
        continue;
      }
      nextSnapshots.set(proposalId, {
        ...snapshot,
        blocks: snapshot.blocks.map((block) => ({
          ...block,
          id: blocksMapping[block.id] ?? block.id,
          parentBlockId: block.parentBlockId
            ? (blocksMapping[block.parentBlockId] ?? block.parentBlockId)
            : block.parentBlockId,
        })),
      });
    }
    set({ clientSnapshots: nextSnapshots });
  },

  setProposals: (proposals) => {
    const sorted = sortProposals(proposals);
    const nextProposalIds = new Set(sorted.map((p) => p.id));

    // Clean up add previews for proposals that are gone or no longer previewable.
    const currentPreviewSectionIds = get().previewSectionIds;
    for (const [proposalId, sectionId] of currentPreviewSectionIds) {
      const proposal = sorted.find((p) => p.id === proposalId);

      if (!proposal) {
        // Proposal gone entirely — remove the add preview section.
        usePageEditorStore.getState().removePreviewSection(sectionId);
        continue;
      }

      if (!isPreviewableAddProposal(proposal)) {
        usePageEditorStore.getState().removePreviewSection(sectionId);
      }
    }

    // Revert targeted proposal previews for proposals that are leaving the list. This restores
    // the section to its original state before we capture snapshots for incoming proposals,
    // so each snapshot reflects the true pre-proposal section state (not a partially-previewed one).
    const currentProposals = get().proposals;
    const currentClientSnapshots = get().clientSnapshots;
    for (const proposal of currentProposals) {
      if (nextProposalIds.has(proposal.id)) continue;
      if (!currentClientSnapshots.has(proposal.id)) continue; // Preview was never applied.
      if (isPreviewableTargetedProposal(proposal) && proposal.targetSectionId) {
        revertTargetedProposalSection(proposal.id);
      }
    }

    // Capture the current section state for NEW targeted proposals before their preview
    // is applied. This gives us an accurate snapshot for revert-on-reject that is independent
    // of the server's beforeSnapshot (which may lag DB writes).
    const newClientSnapshots = new Map(currentClientSnapshots);
    for (const proposal of sorted) {
      if (!isPreviewableTargetedProposal(proposal) || !proposal.targetSectionId) continue;
      if (newClientSnapshots.has(proposal.id)) continue; // Already captured for this proposal.
      const snapshot = captureClientSnapshot(proposal.targetSectionId);
      if (snapshot) {
        newClientSnapshots.set(proposal.id, snapshot);
      }
    }

    // Capture a per-section committed baseline on a section's first proposal and preserve it across churn (survives batch replacement).
    const nextSectionIds = new Set(
      sorted.flatMap((p) =>
        isPreviewableTargetedProposal(p) && p.targetSectionId ? [p.targetSectionId] : [],
      ),
    );
    const nextSectionDiffBaselines = new Map<string, Partial<PageSection>>();
    for (const sectionId of nextSectionIds) {
      const existing = get().sectionDiffBaselines.get(sectionId);
      if (existing) {
        nextSectionDiffBaselines.set(sectionId, existing);
        continue;
      }
      const baseline = captureClientSnapshot(sectionId);
      if (baseline) nextSectionDiffBaselines.set(sectionId, baseline);
    }

    set((state) => ({
      proposals: sorted,
      inFlightIds: new Set([...state.inFlightIds].filter((id) => nextProposalIds.has(id))),
      previewSectionIds: new Map(
        [...state.previewSectionIds].filter(([proposalId]) => {
          const proposal = sorted.find((p) => p.id === proposalId);
          return proposal && isPreviewableAddProposal(proposal);
        }),
      ),
      previewPendingIds: new Set(
        [...state.previewPendingIds].filter((id) => nextProposalIds.has(id)),
      ),
      clientSnapshots: new Map([...newClientSnapshots].filter(([id]) => nextProposalIds.has(id))),
      sectionDiffBaselines: nextSectionDiffBaselines,
    }));

    // Apply previews after state update completes to avoid re-entry.
    for (const proposal of sorted) {
      if (!isPreviewableTargetedProposal(proposal) || !proposal.targetSectionId) continue;
      applySuggestedTargetedProposalSection(proposal.id);
    }

    for (const proposal of sorted) {
      if (!isPreviewableAddProposal(proposal)) {
        continue;
      }

      const { previewSectionIds, previewPendingIds } = get();
      const existingSectionId = previewSectionIds.get(proposal.id);

      if (existingSectionId) {
        usePageEditorStore
          .getState()
          .updateSectionPreview(existingSectionId, toPreviewSectionUpdates(proposal.payload));
        void hydrateDataWidgetBlocksForSection(existingSectionId, { previewOnly: true });
        continue;
      }

      if (previewPendingIds.has(proposal.id)) {
        continue;
      }

      set((state) => ({
        previewPendingIds: new Set([...state.previewPendingIds, proposal.id]),
      }));

      void usePageEditorStore
        .getState()
        .previewAddSection(
          toPageSectionPayload(proposal.payload),
          typeof proposal.payload.rowPosition === 'number'
            ? (proposal.payload.rowPosition as number)
            : undefined,
          { skipDefaultHydration: true },
        )
        .then((newSectionId) => {
          set((state) => {
            const proposalStillPending = state.proposals.some(
              (p) => p.id === proposal.id && isPreviewableAddProposal(p),
            );
            const nextPendingIds = new Set(
              [...state.previewPendingIds].filter((id) => id !== proposal.id),
            );

            if (!proposalStillPending) {
              if (newSectionId) {
                usePageEditorStore.getState().removePreviewSection(newSectionId);
              }
              return { previewPendingIds: nextPendingIds };
            }

            if (!newSectionId) {
              return { previewPendingIds: nextPendingIds };
            }

            const nextPreviewSectionIds = new Map(state.previewSectionIds);
            nextPreviewSectionIds.set(proposal.id, newSectionId);
            scheduleScrollToPageEditorSection(newSectionId);
            void hydrateDataWidgetBlocksForSection(newSectionId, { previewOnly: true });
            return {
              previewPendingIds: nextPendingIds,
              previewSectionIds: nextPreviewSectionIds,
            };
          });
        });
    }
  },

  addProposal: (proposal) => {
    // Skip proposals that were already resolved client-side to prevent race-condition re-adds
    // (e.g. AI service broadcasts a status update after the user has already accepted/rejected).
    if (get().resolvedProposalIds.has(proposal.id)) return;
    const existing = get().proposals.filter((item) => item.id !== proposal.id);
    get().setProposals([...existing, proposal]);
    if (
      proposal.targetSectionId &&
      (proposal.status === 'pending' || proposal.status === 'generating')
    ) {
      scheduleScrollToPageEditorSection(proposal.targetSectionId);
    }
  },

  removeProposal: (proposalId) => {
    const removedProposal = get().proposals.find((p) => p.id === proposalId);
    cleanupPreviewSection(proposalId);
    set((state) => ({
      proposals: state.proposals.filter((p) => p.id !== proposalId),
      inFlightIds: new Set([...state.inFlightIds].filter((id) => id !== proposalId)),
      previewSectionIds: new Map([...state.previewSectionIds].filter(([id]) => id !== proposalId)),
      previewPendingIds: new Set([...state.previewPendingIds].filter((id) => id !== proposalId)),
      clientSnapshots: new Map([...state.clientSnapshots].filter(([id]) => id !== proposalId)),
    }));
    // cleanupPreviewSection does a full section replace (reverts to the removed proposal's baseline),
    // which can wipe previews for other proposals applied after the removed one. Re-apply any
    // remaining proposals for the same section in sorted order so the latest content wins.
    if (removedProposal?.targetSectionId) {
      for (const proposal of sortProposals(get().proposals)) {
        if (proposal.targetSectionId !== removedProposal.targetSectionId) continue;
        if (!isPreviewableTargetedProposal(proposal)) continue;
        applySuggestedTargetedProposalSection(proposal.id);
      }
    }
  },

  getOriginalSnapshot: (proposalId) => originalPreviewUpdatesForProposal(proposalId),

  getSectionDiffBaseline: (sectionId) => get().sectionDiffBaselines.get(sectionId) ?? null,

  hasProposalRunId: (runId) =>
    get().proposals.some((proposal) => payloadContainsRunId(proposal.payload, runId)),

  resolveProposalRunId: (runId, imageUrl) => {
    const proposals = get().proposals;
    let anyChanged = false;
    const updated = proposals.map((proposal) => {
      if (!payloadContainsRunId(proposal.payload, runId)) return proposal;
      const [newPayload, payloadChanged] = resolveRunIdInPayload(proposal.payload, runId, imageUrl);
      if (!payloadChanged) return proposal;
      anyChanged = true;
      const newStatus = payloadHasAnyRunId(newPayload) ? proposal.status : 'pending';
      return { ...proposal, payload: newPayload as Record<string, unknown>, status: newStatus };
    });
    if (!anyChanged) return;
    set({ proposals: updated });

    for (let i = 0; i < updated.length; i++) {
      const proposal = updated[i];
      if (proposal === proposals[i]) continue;
      if (isPreviewableTargetedProposal(proposal) && proposal.targetSectionId) {
        applySuggestedTargetedProposalSection(proposal.id);
      } else if (isPreviewableAddProposal(proposal)) {
        const sectionId = get().previewSectionIds.get(proposal.id);
        if (sectionId) {
          usePageEditorStore
            .getState()
            .updateSectionPreview(sectionId, toPreviewSectionUpdates(proposal.payload));
          void hydrateDataWidgetBlocksForSection(sectionId, { previewOnly: true });
        }
      }
    }
  },

  reloadProposals: async () => {
    const { lastLoadParams } = get();
    if (!lastLoadParams) return;
    await get().loadProposals(
      lastLoadParams.pageId,
      lastLoadParams.pageVersionId,
      lastLoadParams.threadId,
    );
  },

  loadProposals: async (pageId, pageVersionId, threadId) => {
    set({ isLoading: true, lastLoadParams: { pageId, pageVersionId, threadId } });
    try {
      const response = await http.get(cmsPagesEditorAiProposalsPath(pageId), {
        params: {
          ...(pageVersionId ? { pageVersionId } : {}),
          ...(threadId ? { threadId } : {}),
        },
      });
      const proposals = Array.isArray(response.data?.data)
        ? (response.data.data as AiProposal[])
        : [];
      get().setProposals(proposals);
      // Clear resolved IDs after a full server reload — server state is authoritative.
      set({ resolvedProposalIds: new Set() });
    } finally {
      set({ isLoading: false });
    }
  },

  accept: async (proposalId) => {
    const proposal = get().proposals.find((p) => p.id === proposalId);
    if (!proposal || get().inFlightIds.has(proposalId)) return;

    set((state) => ({
      isMutating: true,
      inFlightIds: new Set([...state.inFlightIds, proposalId]),
    }));
    let acceptPhase: 'apply' | 'autosave' | 'proposal_status' = 'apply';
    try {
      let sectionIdToScrollTo: string | null =
        proposal.operation === 'delete' ? null : (proposal.targetSectionId ?? null);

      const hydrationSectionIds = new Set<string>();
      const appliedSectionId = await applyAcceptedProposal(proposal, hydrationSectionIds);
      if (proposal.operation === 'add') sectionIdToScrollTo = appliedSectionId;

      await hydrateDataWidgetBlocksForSections(hydrationSectionIds);

      acceptPhase = 'autosave';
      await flushPendingAutosave();
      acceptPhase = 'proposal_status';
      await http.patch(cmsPagesEditorAiProposalPath(proposal.pageId, proposal.id), {
        status: 'accepted',
      });

      set((state) => ({
        proposals: state.proposals.filter((p) => p.id !== proposalId),
        resolvedProposalIds: new Set([...state.resolvedProposalIds, proposalId]),
        inFlightIds: new Set([...state.inFlightIds].filter((id) => id !== proposalId)),
        previewSectionIds: new Map(
          [...state.previewSectionIds].filter(([id]) => id !== proposalId),
        ),
        previewPendingIds: new Set([...state.previewPendingIds].filter((id) => id !== proposalId)),
        clientSnapshots: new Map([...state.clientSnapshots].filter(([id]) => id !== proposalId)),
        sectionDiffBaselines: pruneSectionDiffBaselines(
          state.proposals.filter((p) => p.id !== proposalId),
          state.sectionDiffBaselines,
        ),
      }));

      if (sectionIdToScrollTo) {
        scheduleScrollToPageEditorSection(sectionIdToScrollTo);
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('[ai-proposals] accept failed', {
          proposalId,
          operation: proposal.operation,
          pageId: proposal.pageId,
          acceptPhase,
          error,
        });
      }

      if (acceptPhase === 'proposal_status') {
        // The content change was already applied and autosaved — the section edit went through.
        // Only the status record update failed. Clean up the proposal from the store regardless,
        // since re-trying accept would re-apply the section change (creating a duplicate edit).
        set((state) => ({
          proposals: state.proposals.filter((p) => p.id !== proposalId),
          resolvedProposalIds: new Set([...state.resolvedProposalIds, proposalId]),
          previewSectionIds: new Map(
            [...state.previewSectionIds].filter(([id]) => id !== proposalId),
          ),
          previewPendingIds: new Set(
            [...state.previewPendingIds].filter((id) => id !== proposalId),
          ),
          clientSnapshots: new Map([...state.clientSnapshots].filter(([id]) => id !== proposalId)),
          sectionDiffBaselines: pruneSectionDiffBaselines(
            state.proposals.filter((p) => p.id !== proposalId),
            state.sectionDiffBaselines,
          ),
        }));
        // If the proposal was already expired server-side (e.g. target section deleted by
        // another user), the content change still went through — no need to alarm the user.
        if (!isProposalAlreadyResolved(error)) {
          toast.error('Change saved, but status could not be updated.');
        }
      } else {
        // apply or autosave phase: the content change may not have gone through.
        // If the editor apply failed after we already reverted the preview, re-apply it so
        // the user sees the diff view again rather than blank original content.
        if (acceptPhase === 'apply' && isPreviewableTargetedProposal(proposal)) {
          applySuggestedTargetedProposalSection(proposalId);
        }
        toast.error('Failed to apply change. Please try again.');
      }
    } finally {
      set((state) => ({
        isMutating: state.inFlightIds.size > 1,
        inFlightIds: new Set([...state.inFlightIds].filter((id) => id !== proposalId)),
      }));
    }
  },

  reject: async (proposalId) => {
    const proposal = get().proposals.find((p) => p.id === proposalId);
    if (!proposal || get().inFlightIds.has(proposalId)) return;

    set((state) => ({
      isMutating: true,
      inFlightIds: new Set([...state.inFlightIds, proposalId]),
    }));
    try {
      await http.patch(cmsPagesEditorAiProposalPath(proposal.pageId, proposal.id), {
        status: 'rejected',
      });

      // Revert any section preview and remove proposal from store.
      cleanupPreviewSection(proposalId);
      // Re-hydrate data widget cards on the reverted target section so dynamic items
      // (which the beforeSnapshot didn't carry) show up again after the revert.
      if (isPreviewableTargetedProposal(proposal) && proposal.targetSectionId) {
        await hydrateDataWidgetBlocksForSection(proposal.targetSectionId);
      }
      set((state) => ({
        proposals: state.proposals.filter((p) => p.id !== proposalId),
        resolvedProposalIds: new Set([...state.resolvedProposalIds, proposalId]),
        inFlightIds: new Set([...state.inFlightIds].filter((id) => id !== proposalId)),
        previewSectionIds: new Map(
          [...state.previewSectionIds].filter(([id]) => id !== proposalId),
        ),
        previewPendingIds: new Set([...state.previewPendingIds].filter((id) => id !== proposalId)),
        clientSnapshots: new Map([...state.clientSnapshots].filter(([id]) => id !== proposalId)),
        sectionDiffBaselines: pruneSectionDiffBaselines(
          state.proposals.filter((p) => p.id !== proposalId),
          state.sectionDiffBaselines,
        ),
      }));
    } catch {
      toast.error('Failed to reject change. Please try again.');
    } finally {
      set((state) => ({
        isMutating: state.inFlightIds.size > 1,
        inFlightIds: new Set([...state.inFlightIds].filter((id) => id !== proposalId)),
      }));
    }
  },

  acceptMany: async (proposalIds) => {
    const proposals = get().proposals.filter((proposal) => proposalIds.includes(proposal.id));
    if (proposals.length === 0) return;
    if (proposals.length === 1) {
      await get().accept(proposals[0].id);
      return;
    }

    const sortedProposals = sortProposals(proposals);
    const executionProposals = orderProposalsForApply(sortedProposals);
    const proposalIdSet = new Set(sortedProposals.map((proposal) => proposal.id));
    const firstTargetedIds = firstTargetedProposalIds(sortedProposals);

    set((state) => ({
      isMutating: true,
      inFlightIds: new Set([...state.inFlightIds, ...proposalIdSet]),
    }));

    try {
      pauseAutosave();
      // Accepting several proposals at once is one user action, so it undoes as one step.
      usePageEditorStore.getState().beginHistoryGroup();
      let sectionIdToScrollTo: string | null = null;
      const hydrationSectionIds = new Set<string>();

      try {
        for (const proposal of executionProposals) {
          const appliedSectionId = await applyAcceptedProposal(
            proposal,
            hydrationSectionIds,
            firstTargetedIds,
          );
          if (proposal.operation !== 'delete') sectionIdToScrollTo ||= appliedSectionId;
        }
      } finally {
        usePageEditorStore.getState().endHistoryGroup();
        resumeAutosave();
      }

      await hydrateDataWidgetBlocksForSections(hydrationSectionIds);
      await flushPendingAutosave();
      await persistBatchStatusByGroup(sortedProposals[0].pageId, sortedProposals, 'accepted');

      set((state) => ({
        proposals: state.proposals.filter((proposal) => !proposalIdSet.has(proposal.id)),
        resolvedProposalIds: new Set([...state.resolvedProposalIds, ...proposalIdSet]),
        inFlightIds: new Set([...state.inFlightIds].filter((id) => !proposalIdSet.has(id))),
        previewSectionIds: new Map(
          [...state.previewSectionIds].filter(([id]) => !proposalIdSet.has(id)),
        ),
        previewPendingIds: new Set(
          [...state.previewPendingIds].filter((id) => !proposalIdSet.has(id)),
        ),
        clientSnapshots: new Map(
          [...state.clientSnapshots].filter(([id]) => !proposalIdSet.has(id)),
        ),
        sectionDiffBaselines: pruneSectionDiffBaselines(
          state.proposals.filter((proposal) => !proposalIdSet.has(proposal.id)),
          state.sectionDiffBaselines,
        ),
      }));

      if (sectionIdToScrollTo) {
        scheduleScrollToPageEditorSection(sectionIdToScrollTo);
      }
    } catch {
      toast.error('Failed to apply changes. Please try again.');
    } finally {
      set((state) => ({
        isMutating: false,
        inFlightIds: new Set([...state.inFlightIds].filter((id) => !proposalIdSet.has(id))),
      }));
    }
  },

  rejectMany: async (proposalIds) => {
    const proposals = get().proposals.filter((proposal) => proposalIds.includes(proposal.id));
    if (proposals.length === 0) return;
    if (proposals.length === 1) {
      await get().reject(proposals[0].id);
      return;
    }

    const sortedProposals = sortProposals(proposals);
    const proposalIdSet = new Set(sortedProposals.map((proposal) => proposal.id));
    const firstTargetedIds = firstTargetedProposalIds(sortedProposals);

    set((state) => ({
      isMutating: true,
      inFlightIds: new Set([...state.inFlightIds, ...proposalIdSet]),
    }));

    try {
      await persistBatchStatusByGroup(sortedProposals[0].pageId, sortedProposals, 'rejected');

      const hydrationSectionIds = new Set<string>();
      for (const proposal of sortedProposals) {
        collectRejectHydration(proposal, hydrationSectionIds, firstTargetedIds);
      }

      await hydrateDataWidgetBlocksForSections(hydrationSectionIds);

      set((state) => ({
        proposals: state.proposals.filter((proposal) => !proposalIdSet.has(proposal.id)),
        resolvedProposalIds: new Set([...state.resolvedProposalIds, ...proposalIdSet]),
        inFlightIds: new Set([...state.inFlightIds].filter((id) => !proposalIdSet.has(id))),
        previewSectionIds: new Map(
          [...state.previewSectionIds].filter(([id]) => !proposalIdSet.has(id)),
        ),
        previewPendingIds: new Set(
          [...state.previewPendingIds].filter((id) => !proposalIdSet.has(id)),
        ),
        clientSnapshots: new Map(
          [...state.clientSnapshots].filter(([id]) => !proposalIdSet.has(id)),
        ),
        sectionDiffBaselines: pruneSectionDiffBaselines(
          state.proposals.filter((proposal) => !proposalIdSet.has(proposal.id)),
          state.sectionDiffBaselines,
        ),
      }));
    } catch {
      toast.error('Failed to reject changes. Please try again.');
    } finally {
      set((state) => ({
        isMutating: false,
        inFlightIds: new Set([...state.inFlightIds].filter((id) => !proposalIdSet.has(id))),
      }));
    }
  },

  acceptAll: async (pageId, batchId) => {
    const pendingProposals = get().proposals.filter(
      (proposal) =>
        proposal.pageId === pageId &&
        isActiveProposalStatus(proposal.status) &&
        (batchId ? proposal.batchId === batchId : true),
    );

    if (pendingProposals.length === 0) return true;

    const executionProposals = orderProposalsForApply(pendingProposals);
    const proposalIds = new Set(pendingProposals.map((p) => p.id));
    const firstTargetedIds = firstTargetedProposalIds(pendingProposals);
    set((state) => ({
      isMutating: true,
      inFlightIds: new Set([...state.inFlightIds, ...proposalIds]),
    }));
    try {
      pauseAutosave();
      // Accepting a batch is one user action, so it undoes as one step.
      usePageEditorStore.getState().beginHistoryGroup();
      const hydrationSectionIds = new Set<string>();
      try {
        for (const proposal of executionProposals) {
          await applyAcceptedProposal(proposal, hydrationSectionIds, firstTargetedIds);
        }
      } finally {
        usePageEditorStore.getState().endHistoryGroup();
        resumeAutosave();
      }

      await hydrateDataWidgetBlocksForSections(hydrationSectionIds);

      await flushPendingAutosave();
      await persistBatchStatusByGroup(pageId, pendingProposals, 'accepted');

      set((state) => ({
        proposals: state.proposals.filter((p) => !proposalIds.has(p.id)),
        resolvedProposalIds: new Set([...state.resolvedProposalIds, ...proposalIds]),
        inFlightIds: new Set([...state.inFlightIds].filter((id) => !proposalIds.has(id))),
        previewSectionIds: new Map(
          [...state.previewSectionIds].filter(([id]) => !proposalIds.has(id)),
        ),
        previewPendingIds: new Set(
          [...state.previewPendingIds].filter((id) => !proposalIds.has(id)),
        ),
        clientSnapshots: new Map([...state.clientSnapshots].filter(([id]) => !proposalIds.has(id))),
        sectionDiffBaselines: pruneSectionDiffBaselines(
          state.proposals.filter((p) => !proposalIds.has(p.id)),
          state.sectionDiffBaselines,
        ),
      }));
      return true;
    } catch {
      toast.error('Failed to apply changes. Please try again.');
      return false;
    } finally {
      set((state) => ({
        isMutating: false,
        inFlightIds: new Set([...state.inFlightIds].filter((id) => !proposalIds.has(id))),
      }));
    }
  },

  rejectAll: async (pageId, batchId) => {
    const pendingProposals = get().proposals.filter(
      (proposal) =>
        proposal.pageId === pageId &&
        isActiveProposalStatus(proposal.status) &&
        (batchId ? proposal.batchId === batchId : true),
    );

    if (pendingProposals.length === 0) return;

    const proposalIds = new Set(pendingProposals.map((p) => p.id));
    const firstTargetedIds = firstTargetedProposalIds(pendingProposals);
    set((state) => ({
      isMutating: true,
      inFlightIds: new Set([...state.inFlightIds, ...proposalIds]),
    }));
    try {
      await persistBatchStatusByGroup(pageId, pendingProposals, 'rejected');

      const hydrationSectionIds = new Set<string>();
      for (const proposal of pendingProposals) {
        collectRejectHydration(proposal, hydrationSectionIds, firstTargetedIds);
      }

      await hydrateDataWidgetBlocksForSections(hydrationSectionIds);

      set((state) => ({
        proposals: state.proposals.filter((p) => !proposalIds.has(p.id)),
        resolvedProposalIds: new Set([...state.resolvedProposalIds, ...proposalIds]),
        inFlightIds: new Set([...state.inFlightIds].filter((id) => !proposalIds.has(id))),
        previewSectionIds: new Map(
          [...state.previewSectionIds].filter(([id]) => !proposalIds.has(id)),
        ),
        previewPendingIds: new Set(
          [...state.previewPendingIds].filter((id) => !proposalIds.has(id)),
        ),
        clientSnapshots: new Map([...state.clientSnapshots].filter(([id]) => !proposalIds.has(id))),
        sectionDiffBaselines: pruneSectionDiffBaselines(
          state.proposals.filter((p) => !proposalIds.has(p.id)),
          state.sectionDiffBaselines,
        ),
      }));
    } catch {
      toast.error('Failed to apply changes. Please try again.');
    } finally {
      set((state) => ({
        isMutating: false,
        inFlightIds: new Set([...state.inFlightIds].filter((id) => !proposalIds.has(id))),
      }));
    }
  },
}));
